/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/naming-convention */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatBadgeModule } from '@angular/material/badge';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  ButtonDirective,
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
  WcgInputHeight,
  WcgMenuOption,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';

import {
  EditModeDirective,
  InlineEditorComponent,
  ViewModeDirective,
} from '@erm/cluster';

import {
  DocumentTypesFormArray,
  IrisSettingsForm,
  SDocumentTypePayload,
  SubmissionDialogData,
  SubmissionDialogForm,
  SubmissionDialogFormData,
} from '../../../types';
import {
  fetchCreatedDocuments,
  filterUpdateListDocuments,
  mapIrisSettings,
} from '../../../utils';
import { CONFIGURATION_SUBMISSION_TYPE_ACTIONS } from '../../../actions';
import { distinctUntilChanged } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgDialogComponent,
    MatTabsModule,
    NgTemplateOutlet,
    WcgButtonComponent,
    MatIcon,
    MatButton,
    ButtonDirective,
    WcgUniSelectComponent,
    InlineEditorComponent,
    EditModeDirective,
    ViewModeDirective,
    WcgInputFieldComponent,
    MatBadgeModule,
  ],
  selector: 'wcg-submissions-dialog',
  styleUrls: ['./submissions-dialog.component.scss'],
  templateUrl: './submissions-dialog.component.html',
})
export class SubmissionsDialogComponent implements OnInit {
  public readonly data = inject<SubmissionDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(
    MatDialogRef<SubmissionsDialogComponent>,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _store = inject(Store);
  private readonly _storeActions = inject(Actions);

  private _$trackUpdatedFormValues = signal<SDocumentTypePayload[]>([]);
  private _$trackRemovedFormValues = signal<SDocumentTypePayload[]>([]);
  private _$trackAddedFormGroupsIndex = signal<number>(0);

  submissionDialogForm = this._createForm();

  $inputHeight = signal<typeof WcgInputHeight>(WcgInputHeight);
  $currentValidationError = signal<string>('');
  $documentTypesArray = signal<FormArray<DocumentTypesFormArray>>(
    this.submissionDialogForm.controls?.documentTypes,
  );
  $documentTypes: Signal<WcgMenuOption[]> = computed(() => {
    if (!this.data.documentTypes()?.length) {
      return [];
    }
    return this.data.documentTypes()?.map((d) => ({ id: d.id, label: d.name }));
  });
  $title = computed(() => {
    return `Edit ${this.data.submissionDocument().name}`;
  });

  $attributes: Signal<WcgMenuOption[]> = computed(() => {
    if (!this.data.attributes().length) {
      return [];
    }
    return this.data
      .attributes()
      .map((a) => ({ id: a.attribute_type_id, label: a.attribute_type_name }));
  });

  $currentTab = signal<'documentType' | 'irisSettings'>('documentType');

  ngOnInit(): void {
    this._patchDocumentTypes();
    this._patchIrisSettings();
    this._customListeners();
  }

  save(): void {
    const irisMapping = mapIrisSettings(
      this.submissionDialogForm.value as SubmissionDialogFormData,
    );
    this._store.dispatch(
      CONFIGURATION_SUBMISSION_TYPE_ACTIONS.updateIrisSettings({
        irisMapping: irisMapping,
        id: this.data.submissionDocument().id,
        wcgTableValues: this.data.wcgTableValues,
        entityType: this.data.entityType,
      }),
    );
    this._dispatchSubmissionActions();
  }

  cancel(): void {
    this._dialogRef.close();
  }

  addDocumentType(): void {
    this._addDocumentTypes();
  }

  deleteDocumentType(index: number): void {
    const document_type_id =
      this.$documentTypesArray().at(index).controls.documentType.value?.id;

    if (document_type_id && index > this._$trackAddedFormGroupsIndex() - 1) {
      this._$trackRemovedFormValues.update((arr) => [
        ...arr,
        {
          action: 'remove',
          document_type_id: document_type_id,
        },
      ]);
      this._$trackUpdatedFormValues.update((arr) =>
        arr.filter((v) => v.document_type_id !== document_type_id),
      );
    } else {
      this._$trackAddedFormGroupsIndex.update((index) => index - 1);
    }
    this.$documentTypesArray().removeAt(index);
  }

  tabChange(currentTab: MatTabChangeEvent): void {
    if (currentTab.index === 1) {
      this.$currentTab.set('irisSettings');
    } else {
      this.$currentTab.set('documentType');
    }
  }

  updateAttributeType<T>(
    type: 'DocumentType' | 'AttributeType',
    $event: T,
    documentId: string,
  ): void {
    const val = $event as WcgMenuOption;
    const updateAttributeValue = (attributeId: string, document_id: string) => {
      this._$trackUpdatedFormValues().push({
        action: 'update',
        attribute_type_id: val.id,
        document_type_id: document_id,
      });
    };

    if (type === 'AttributeType' && val.id && documentId) {
      if (!this._$trackUpdatedFormValues().length) {
        updateAttributeValue(val.id, documentId);
      } else if (
        !this._$trackUpdatedFormValues().some(
          (v) => v.document_type_id === documentId,
        )
      ) {
        updateAttributeValue(val.id, documentId);
      } else {
        this._$trackUpdatedFormValues.update((arr) =>
          arr.map((a) =>
            a.document_type_id === documentId
              ? {
                  ...a,
                  attribute_type_id: val.id,
                }
              : a,
          ),
        );
      }
    }
  }

  /**
   * @Creating Form
   * **/
  private _createForm() {
    return this._formBuilder.group<SubmissionDialogForm>({
      documentTypes: this._formBuilder.array<DocumentTypesFormArray>([]),
      irisSettings: this._formBuilder.group<IrisSettingsForm>({
        envelopeType: new FormControl<string>('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        routingGroup: new FormControl<WcgMenuOption | null>(null, {
          nonNullable: false,
          validators: [Validators.required],
        }),
      }),
    });
  }
  /**
   * @Creating Form End
   * */

  /**
   * @Handling IrisSettings
   * */
  private _patchIrisSettings(): void {
    const irisSettings = this.data.submissionDocument().iris_mapping;
    if (!irisSettings) {
      return;
    }
    this.submissionDialogForm.controls.irisSettings.patchValue(
      {
        envelopeType: irisSettings.envelope_type.name,
        routingGroup: {
          id: irisSettings.routing_group.id.toString(),
          label: irisSettings.routing_group.name,
        },
      },
      { emitEvent: false },
    );
  }

  /**
   * @Handling DocumentsArray
   * */
  private _addDocumentTypes(): void {
    const documentFormGroup = this._constructDocumentTypeGroup();
    this.$documentTypesArray.update((dt) => {
      if (dt.length <= 0) {
        dt.removeAt(0);
      }
      dt.insert(0, documentFormGroup);
      return dt;
    });
    this._$trackAddedFormGroupsIndex.update((index) => index + 1);
    /**
     * Since we're subscribing to each documentType formControl.
     * We will need to manually update the subscriptions every time
     * we add new formGroup to the documentTypesArrayp
     * */
    this._formArrayListener();
  }

  private _patchDocumentTypes(): void {
    if (!this.data.submissionDocument().document_types.length) {
      return;
    }
    this.$documentTypesArray.update((arr) => {
      for (const doc of this.data.submissionDocument().document_types) {
        if (doc) {
          const documentType = {
            id: doc.id,
            label: doc.name,
          };
          const attributeType = {
            id: doc.attribute_type_id,
            label: doc.attribute_type_name,
          };
          arr.push(
            this._constructDocumentTypeGroup(documentType, attributeType),
          );
        }
      }
      return arr;
    });
    this._formArrayListener();
  }

  private _constructDocumentTypeGroup(
    documentType?: WcgMenuOption,
    attributeType?: WcgMenuOption,
  ) {
    return this._formBuilder.group({
      attribute: new FormControl<WcgMenuOption | null>(attributeType ?? null, {
        nonNullable: false,
        validators: [Validators.required],
      }),
      documentType: new FormControl<WcgMenuOption | null>(
        documentType ?? null,
        {
          nonNullable: false,
          validators: [Validators.required],
        },
      ),
    });
  }
  /**
   * @Handling DocumentsArray End
   * */
  /**
   * @FormArray Listener
   * */
  private _formArrayListener(): void {
    this.$documentTypesArray().controls.forEach((control) => {
      control.controls.documentType.valueChanges
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (value) => {
            const arr = this.$documentTypesArray()
              .getRawValue()
              .map((d) => d.documentType);

            if (arr.filter((a) => a?.id === value?.id).length > 1) {
              control.controls.documentType.setErrors({
                error: 'Document Type must be unique',
              });
              this.$currentValidationError.set('Document Type must be unique');
            }
          },
        });
    });
  }

  /**
   * @FormArray Listener End
   */
  /*
   * @Custom Listeners
   * */
  private _customListeners(): void {
    this.submissionDialogForm.controls.irisSettings.controls.routingGroup.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () =>
          this.submissionDialogForm.controls.irisSettings.controls.envelopeType.reset(
            '',
            { emitEvent: false },
          ),
      });
    this._storeActions
      .pipe(
        ofType(
          ...[
            CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateIrisSettingsSuccess,
            CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateSubmissionDocumentTypesSuccess,
          ],
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: () => this._dialogRef.close(),
      });
  }
  /**
   * @Custom ListenersEnd
   * */

  private _dispatchSubmissionActions(): void {
    const entityType = this.data.entityType;
    const createDocumentTypeList = fetchCreatedDocuments(
      this.$documentTypesArray(),
      this._$trackAddedFormGroupsIndex(),
    );
    const updateList =
      createDocumentTypeList()?.length > 0 &&
      this._$trackUpdatedFormValues()?.length > 0
        ? filterUpdateListDocuments(
            createDocumentTypeList(),
            this._$trackUpdatedFormValues(),
          )
        : this._$trackUpdatedFormValues();

    if (
      updateList.length > 0 ||
      createDocumentTypeList().length > 0 ||
      this._$trackRemovedFormValues().length > 0
    ) {
      this._store.dispatch(
        CONFIGURATION_SUBMISSION_TYPE_ACTIONS.updateSubmissionDocumentTypes({
          entityType,
          payload: [
            ...updateList,
            ...createDocumentTypeList(),
            ...this._$trackRemovedFormValues(),
          ],
          submissionId: this.data.submissionDocument().id,
          wcgTableValues: this.data.wcgTableValues,
        }),
      );
    }
  }
}
